
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

const BloubergstrandPage: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for Bloubergstrand page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Bloubergstrand Painters",
      "description": "Professional painting contractors in Bloubergstrand, Western Cape. Expert coastal painting services with Table Mountain views. Marine-grade coatings and weather protection.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/bloubergstrand-painters`,
      "telephone": "",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Bloubergstrand",
        "addressRegion": "Western Cape",
        "addressCountry": "ZA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-33.8116",
        "longitude": "18.4906"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "-33.8116",
          "longitude": "18.4906"
        },
        "geoRadius": "5000"
      },
      "services": [
        "Interior Painting Bloubergstrand",
        "Exterior Painting Bloubergstrand", 
        "Roof Painting Bloubergstrand",
        "Commercial Painting Bloubergstrand",
        "Beachfront Property Painting Bloubergstrand"
      ],
      "areaServed": "Bloubergstrand, Western Cape, South Africa"
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Bloubergstrand Painters - Professional Beachfront Painting Contractors Western Cape | Get Quote";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting contractors in Bloubergstrand, Western Cape. Expert beachfront property painting with iconic Table Mountain views. Specialized marine coatings and coastal protection. Get your free quote today.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Bloubergstrand painters, beachfront painting contractors, coastal painting Western Cape, marine coatings, Table Mountain views');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Interior Painting',
      description: 'Transform your Bloubergstrand home with professional interior painting that maximizes ocean views.',
      link: '/interior-painting'
    },
    {
      icon: 'ri-building-line',
      title: 'Exterior Painting',
      description: 'Protect your Bloubergstrand property with premium marine-grade exterior painting solutions.',
      link: '/exterior-painting'
    },
    {
      icon: 'ri-home-2-line',
      title: 'Roof Painting',
      description: 'Specialized roof painting for Bloubergstrand\'s extreme coastal environment and wind exposure.',
      link: '/roof-painting'
    },
    {
      icon: 'ri-office-building-line',
      title: 'Commercial Painting',
      description: 'Professional commercial painting services for Bloubergstrand businesses and hospitality venues.',
      link: '/commercial-painting'
    },
  ];

  const whyChooseUs = [
    {
      icon: 'ri-camera-line',
      title: 'Iconic Location Expertise',
      description: 'Our Bloubergstrand contractors understand painting for properties with world-famous Table Mountain views.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Extreme Weather Protection',
      description: 'Advanced coatings designed for Bloubergstrand\'s harsh coastal winds and salt exposure.',
    },
    {
      icon: 'ri-time-line',
      title: 'Weather-Dependent Scheduling',
      description: 'Flexible scheduling that works around Bloubergstrand\'s unpredictable coastal weather patterns.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Premium Value',
      description: 'Competitive pricing for high-end coastal painting services in Bloubergstrand.',
    },
  ];

  const bloubergFeatures = [
    {
      icon: 'ri-camera-2-line',
      title: 'Postcard Perfect Views',
      description: 'Color schemes that enhance Bloubergstrand\'s world-famous Table Mountain photography backdrop.'
    },
    {
      icon: 'ri-windy-line',
      title: 'Wind Resistance',
      description: 'Specialized coatings designed for Bloubergstrand\'s notorious strong coastal winds.'
    },
    {
      icon: 'ri-water-percent-line',
      title: 'Salt Spray Protection',
      description: 'Maximum protection against Bloubergstrand\'s intense salt spray and ocean exposure.'
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Tourism Industry Focus',
      description: 'Commercial painting solutions for Bloubergstrand\'s thriving tourism and hospitality sector.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/b1185e80c6cece3ac56277753586143f.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
            <span className="block">Bloubergstrand</span>
            <span className="block text-[#e9f638]">PAINTERS</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Professional beachfront painting contractors serving Bloubergstrand and surrounding areas.
          </p>
          <p className="text-lg md:text-xl mb-12 max-w-4xl mx-auto opacity-90">
            Connect with Bloubergstrand's leading coastal painting specialists. We deliver exceptional 
            results for properties with world-famous Table Mountain views and extreme coastal exposure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="text-black px-8 py-4 rounded-full text-lg font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#e9f638' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d4e532'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e9f638'}
            >
              Get Free Bloubergstrand Quote
            </Link>
            <a 
              href="#services" 
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-black transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              View Services
            </a>
          </div>
        </div>
      </section>

      {/* About Bloubergstrand Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                World-Class Painting Services in Bloubergstrand
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Bloubergstrand, home to the world's most photographed view of Table Mountain, presents 
                the ultimate challenge for coastal painting contractors. This iconic beachfront location 
                experiences some of the Western Cape's most extreme weather conditions, including powerful 
                south-easter winds, intense salt spray, and constant UV exposure from both direct sunlight 
                and reflection off the white sand beaches.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                From the luxury beachfront apartments with panoramic ocean views to the charming beach 
                cottages and bustling commercial establishments that serve international tourists, our 
                Bloubergstrand painting contractors understand the unique demands of this world-renowned 
                location. We provide the highest grade marine painting solutions available.
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Bloubergstrand's Extreme Coastal Environment and Premium Painting Requirements
              </h3>
              <p className="text-lg text-gray-600">
                The combination of constant ocean exposure, powerful winds, and the area's status as 
                a premier tourist destination means Bloubergstrand properties require the absolute 
                best in coastal protection and aesthetic appeal. Our contractors use only the most 
                advanced marine-grade coating systems available in the industry.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Stunning%20beachfront%20properties%20in%20Bloubergstrand%20South%20Africa%20with%20Table%20Mountain%20view%20across%20the%20bay%2C%20professional%20painting%20work%20visible%20on%20luxury%20coastal%20homes%2C%20white%20sand%20beach%20and%20crystal%20blue%20ocean%2C%20world%20famous%20scenic%20location&width=600&height=400&seq=bloubergstrand-beachfront-homes&orientation=landscape"
                alt="Bloubergstrand beachfront properties with professional painting services"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Premium Beachfront Painting Services in Bloubergstrand
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our Bloubergstrand painting contractors provide world-class coastal painting solutions for iconic properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Link 
                key={index} 
                to={service.link}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 border border-gray-100 group cursor-pointer"
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                  <i className={`${service.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-[#e9f638] transition-colors duration-200">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bloubergstrand Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Specialized Solutions for Bloubergstrand's Unique Environment
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced coating systems designed specifically for Bloubergstrand's extreme coastal conditions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {bloubergFeatures.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                  <i className={`${feature.icon} text-2xl text-black`}></i>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h4>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bloubergstrand Specific Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Bloubergstrand Painting Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting services for every type of Bloubergstrand property and coastal challenge
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Bloubergstrand Luxury Beachfront Painting
              </h4>
              <p className="text-gray-600 mb-4">
                Premium painting services for Bloubergstrand's most exclusive beachfront properties. 
                Our contractors use the finest marine-grade coatings available, specifically formulated 
                to withstand the area's extreme salt exposure, powerful winds, and intense UV radiation 
                while maintaining the pristine appearance expected in this world-famous location.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Premium marine coatings
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Extreme weather protection
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Luxury finish standards
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Bloubergstrand Tourism Industry Painting
              </h4>
              <p className="text-gray-600 mb-4">
                Specialized commercial painting for Bloubergstrand's thriving tourism industry, including 
                restaurants, hotels, guesthouses, and retail establishments. We understand the importance 
                of maintaining an attractive appearance for international visitors while ensuring 
                maximum durability in the harsh coastal environment.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Tourism-focused aesthetics
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Minimal business disruption
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  International standards
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Bloubergstrand Wind-Resistant Coating Systems
              </h4>
              <p className="text-gray-600 mb-4">
                Advanced coating systems specifically designed for Bloubergstrand's notorious strong 
                winds and sand-blasting conditions. Our contractors use specialized application 
                techniques and wind-resistant formulations that maintain their integrity and 
                appearance despite constant exposure to the area's challenging weather patterns.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Wind-resistant formulations
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Sand-blast protection
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Weather-timing expertise
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Bloubergstrand Painting Specialists
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              World-class expertise and extreme coastal environment specialization for all Bloubergstrand projects
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                  <i className={`${item.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bloubergstrand Areas Covered */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Bloubergstrand Areas We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive beachfront painting services throughout Bloubergstrand and surrounding coastal areas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Bloubergstrand Beachfront</h5>
              <p className="text-gray-600">
                Prime oceanfront properties with direct beach access and world-famous Table Mountain 
                views requiring the ultimate in marine coating protection and aesthetic excellence.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Bloubergstrand Village</h5>
              <p className="text-gray-600">
                The heart of Bloubergstrand's tourism district with restaurants, shops, and accommodation 
                facilities requiring commercial-grade coastal painting solutions and tourist-focused aesthetics.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Bloubergstrand Residential</h5>
              <p className="text-gray-600">
                Established residential neighborhoods featuring a mix of beach cottages and modern homes, 
                each requiring specialized coastal protection and view-enhancing color schemes.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Bloubergstrand Dunes</h5>
              <p className="text-gray-600">
                Properties near the protected dune areas requiring environmentally sensitive painting 
                approaches that complement the natural coastal landscape and vegetation.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Bloubergstrand Commercial Strip</h5>
              <p className="text-gray-600">
                Main commercial area serving tourists and locals, requiring durable commercial coatings 
                that maintain their appearance despite high foot traffic and coastal exposure.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h6 className="text-lg font-semibold text-gray-900 mb-3">Greater Bloubergstrand Area</h6>
              <p className="text-gray-600">
                Extended service coverage including nearby coastal developments and inland properties 
                that benefit from our specialized extreme coastal environment painting expertise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Related Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our complete range of professional painting services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link 
              to="/interior-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Interior Painting
              </h3>
            </Link>

            <Link 
              to="/exterior-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Exterior Painting
              </h3>
            </Link>

            <Link 
              to="/roof-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-2-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Roof Painting
              </h3>
            </Link>

            <Link 
              to="/commercial-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-office-building-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Commercial Painting
              </h3>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Bloubergstrand Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied customers throughout Bloubergstrand's world-famous coastal community
            </p>
          </div>
          <TestimonialsSlider />
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Your Free Bloubergstrand Painting Quote
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to enhance your Bloubergstrand beachfront property? Connect with world-class painting specialists today
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      <Footer />
      
      {/* Elfsight AI Chatbot */}
      <div>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>
      </div>
    </div>
  );
};

export default BloubergstrandPage;
